import React, { useCallback, useRef, useState } from 'react';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';

import {
  BRIGHT_HORIZONS_CREDITS_DONT_APPLY,
  BRIGHT_HORIZONS_CREDITS_DONT_APPLY_TEXT,
} from '@rover/components/src/callouts/CalloutBadge/CalloutBadge.constants';
import usePriceDisplayInfo from '@rover/components/src/cards/SearchResultCard/PriceColumn/usePriceDisplayInfo';
import useShortlist from '@rover/components/src/shortlist/hooks/useShortlist';
import { DEFAULT_CURRENCY_ISO } from '@rover/constants/src/i18n/language';
import { Box, Flex } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';
import { CLOSE_TIMEOUT } from '@rover/kibble/official-patterns/BottomSheet/BottomSheet';
import useLocationContext from '@rover/react-lib/src/context/useLocationContext';
import useRefocusIfOpenerGone from '@rover/react-lib/src/hooks/useRefocusIfOpenerGone';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';
import { getIsPriceTransparency } from '@rover/utilities/priceTransparency';

import ContactButton from '../../../components/ContactButton';
import { useIsAtPageTop } from '../../hooks/useIsAtPageTop';
import { useRevampBrightHorizonsBanner } from '../../hooks/useRevampBrightHorizonsBanner';
import { useRevampCta } from '../../hooks/useRevampCta';
import { useRevampCtaCommon } from '../../hooks/useRevampCtaCommon';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import { useRevampSelectedDates } from '../../hooks/useRevampSelectedDates';
import { BHSitterProfileBanner } from '../BHSitterProfileBanner/BHSitterProfileBanner';

import { isServiceAway } from './awayService';
import RevampCtaPtBanner from './RevampCtaPtBanner';
import RevampDatesSheet from './RevampDatesSheet';
import RevampMobileCtaBarDetails from './RevampMobileCtaBarDetails';
import RevampPriceDisplaySheet from './RevampPriceDisplaySheet';
import { CTAPropsCommon } from './RevampServicesCta';
import useSaveRevampDates from './useSaveRevampDates';
import { getRangeEndDate, getSitterSlugFromWebUrl } from './utils';

type Props = CTAPropsCommon & {
  handleOpenSheet: () => void;
  selectedDate: Date;
  selectedEndDate?: Date;
  hasDateRange?: boolean;
};

const getPriceUnitWithPets = (afterPriceText: string, petLabel: string): MessageDescriptor =>
  msg`${afterPriceText} for ${petLabel}`;

// The contact list's "Add to contact list" gets its own row; the plain "Contact" fits beside the price.
const STACKED_FOOTER = { flexDirection: 'column', gap: '5x' } as const;
const INLINE_FOOTER = { alignItems: 'center', gap: '4x' } as const;
const INLINE_CONTACT_BUTTON = {
  width: 'auto',
  px: '8x',
  minWidth: '140px',
  flexShrink: '0',
} as const;

const RevampMobileCtaBarPriceDisplay = ({
  handleOpenSheet,
  priceTransparencyLevel,
  locale,
  petCount,
  petType,
  bookingFeePercentage,
  selectedDate,
  selectedEndDate,
  hasDateRange,
}: Props): JSX.Element => {
  const { i18n, t } = useLingui();
  const { contactUrl, showSittersRates } = useRevampProfileContext();
  const {
    sitterData,
    selectedService,
    serviceDisplayName,
    isBrightHorizonsUser,
    isSelectedServiceUnapproved,
    isContactUnavailable,
  } = useRevampCta();
  const { rate, price, petLabel } = useRevampCtaCommon(
    priceTransparencyLevel,
    petType,
    petCount,
    selectedService
  );
  const showPetsInPrice = getIsPriceTransparency(priceTransparencyLevel);
  const currentUser = useCurrentUser();
  const location = useLocationContext();
  const { isVisible: isShortlistVisible, isAdded } = useShortlist();
  const isShortlisted = isAdded(sitterData.opk);
  const sitterSlug = getSitterSlugFromWebUrl(sitterData.webUrl);

  const handleDatesSave = useSaveRevampDates(locale);

  const isAtPageTop = useIsAtPageTop();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isDatesSheetOpen, setIsDatesSheetOpen] = useState(false);

  const detailsButtonRef = useRef<HTMLButtonElement>(null);
  const contactButtonRef = useRef<HTMLElement>(null);
  // Adding or removing the sitter from the sheet swaps the footer buttons. Wait for the sheet's
  // close transition, since the outgoing sheet restores focus as it unmounts.
  const rememberSheetOpener = useRefocusIfOpenerGone({
    isOpen: isExpanded,
    getTarget: () => detailsButtonRef.current ?? contactButtonRef.current,
    delay: CLOSE_TIMEOUT,
  });

  const handleExpand = useCallback(() => {
    rememberSheetOpener(
      // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    );
    setIsExpanded(true);
  }, [rememberSheetOpener]);
  const handleCollapse = useCallback(() => setIsExpanded(false), []);

  const handleDatesPress = useCallback(() => setIsDatesSheetOpen(true), []);
  const handleDatesSheetClose = useCallback(() => setIsDatesSheetOpen(false), []);

  const priceInCredits = rate?.priceInCredits != null ? Number(rate.priceInCredits) : null;
  const shouldShowPriceInCredits =
    isBrightHorizonsUser && !showSittersRates && priceInCredits != null;

  const { brightHorizonsBanner, isShowingBhBanner } = useRevampBrightHorizonsBanner(
    petType,
    petCount
  );

  const {
    formattedPrice,
    beforePriceText,
    afterPriceText: priceUnit,
    showBrightHorizonsCalloutBadge,
  } = usePriceDisplayInfo({
    currencyCode: rate?.pricePerUnitObj.currencyIso || DEFAULT_CURRENCY_ISO,
    priceTransparencyLevel,
    locale,
    price: price ?? '',
    priceInCredits,
    shouldShowPriceInCredits,
    showFromText: !shouldShowPriceInCredits,
    unitAsSentence: rate?.unitAsSentence ?? '',
    isBHUser: isBrightHorizonsUser,
  });

  const {
    allowsLooseDates,
    allowsRecurring,
    looseDates,
    isRecurring,
    weekDays,
    hasDates,
    datesLabel: selectedDatesLabel,
  } = useRevampSelectedDates(locale, selectedDate, selectedEndDate, hasDateRange);
  const datesLabel = hasDates ? selectedDatesLabel : t`Add dates`;
  const serviceDisplayLabel = serviceDisplayName ?? t`Select a service`;
  const comingSoonLabel = t`Coming Soon`;
  const awayLabel = t`Away`;
  const isAway = isServiceAway(selectedService);
  const serviceLabel = isAway ? `${serviceDisplayLabel} (${awayLabel})` : serviceDisplayLabel;

  const afterPriceText = showPetsInPrice
    ? i18n._(getPriceUnitWithPets(priceUnit, petLabel))
    : priceUnit;
  const detailsLabel = isSelectedServiceUnapproved
    ? t`${comingSoonLabel}, ${serviceLabel}, ${datesLabel}. See booking details`
    : t`${beforePriceText} ${formattedPrice} ${afterPriceText}, ${serviceLabel}, ${datesLabel}. See booking details`;

  return (
    <>
      {!isShortlisted && (
        <>
          {!isExpanded && isAtPageTop && (
            <RevampCtaPtBanner
              priceTransparencyLevel={priceTransparencyLevel}
              petCount={petCount}
              petType={petType}
              bookingFeePercentage={bookingFeePercentage}
              variant="bar"
              onPress={handleExpand}
            />
          )}

          {showBrightHorizonsCalloutBadge && (
            <Box pb="2x">
              <Badge
                {...BRIGHT_HORIZONS_CREDITS_DONT_APPLY}
                text={i18n._(BRIGHT_HORIZONS_CREDITS_DONT_APPLY_TEXT)}
              />
            </Box>
          )}

          {isShowingBhBanner && (
            <BHSitterProfileBanner brightHorizonsBanner={brightHorizonsBanner} />
          )}
        </>
      )}

      {/* One ContactButton in both states, so adding the sitter keeps its focus handling. */}
      <Flex {...(isShortlistVisible ? STACKED_FOOTER : INLINE_FOOTER)}>
        {!isShortlisted && (
          <RevampMobileCtaBarDetails
            buttonRef={detailsButtonRef}
            onPress={handleExpand}
            isExpanded={isExpanded}
            label={detailsLabel}
            comingSoonLabel={isSelectedServiceUnapproved ? comingSoonLabel : undefined}
            price={
              rate && price
                ? {
                    beforeText: beforePriceText,
                    formatted: formattedPrice,
                    afterText: afterPriceText,
                  }
                : undefined
            }
            serviceLabel={serviceDisplayLabel}
            isAway={isAway}
            datesLabel={datesLabel}
          />
        )}
        <ContactButton
          sitterInfo={sitterData}
          sitterSlug={sitterSlug}
          contactUrl={contactUrl}
          isLoggedIn={currentUser.isAuthenticated}
          location={location}
          pagePosition="sticky-footer"
          shouldShowRevampLayout
          disabled={isContactUnavailable}
          primaryButtonRef={contactButtonRef}
          {...(isShortlistVisible ? {} : INLINE_CONTACT_BUTTON)}
        />
      </Flex>

      <RevampPriceDisplaySheet
        isOpen={isExpanded}
        onClose={handleCollapse}
        onServicePress={handleOpenSheet}
        onDatesPress={handleDatesPress}
        priceTransparencyLevel={priceTransparencyLevel}
        locale={locale}
        petCount={petCount}
        petType={petType}
        bookingFeePercentage={bookingFeePercentage}
        selectedDate={selectedDate}
        selectedEndDate={selectedEndDate}
        hasDateRange={hasDateRange}
      />

      <RevampDatesSheet
        isOpen={isDatesSheetOpen}
        onClose={handleDatesSheetClose}
        onSave={handleDatesSave}
        locale={locale}
        startDate={hasDateRange ? selectedDate : undefined}
        endDate={hasDateRange ? getRangeEndDate(selectedDate, selectedEndDate) : undefined}
        dates={looseDates}
        isRecurring={isRecurring}
        weekDays={weekDays}
        allowsLooseDates={allowsLooseDates}
        allowsRecurring={allowsRecurring}
      />
    </>
  );
};

export default RevampMobileCtaBarPriceDisplay;
